import React, { useEffect } from 'react';
import { Form, InputNumber, Row, Col, Typography, Tooltip } from 'antd';
import PensionContributionInput from './PensionContributionInput';

const { Title } = Typography;

const CalculatorForm = ({ 
  inputs = {}, 
  onInputChange, 
  onCalculate,
  // New configuration props with sensible defaults
  showTitle = true,
  showBaseSalary = true,
  showBonus = true,
  showMedicalBenefit = true,
  showVoluntaryDeductions = true,
  showEmployeePensionContribution = true,
  showEmployerMaxPensionContribution = true,
  // New: configurable grid column props
  colProps = { xs: 24, sm: 12, md: 8 },
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (inputs) {
      form.setFieldsValue(inputs);
    }
  }, [inputs, form]);

  const handleChange = (fieldName, value) => {
    const numericFields = ['baseSalary', 'employeePensionContribution', 'employerMaxPensionContributionPct', 'bonus', 'medicalBenefit', 'voldeductions'];
    // Preserve empty while typing; avoid coercing '' to 0
    const isEmpty = value === '' || value === null || value === undefined;
    const processedValue = numericFields.includes(fieldName)
      ? (isEmpty ? null : (typeof value === 'number' ? value : parseFloat(value)))
      : value;
    form.setFieldValue(fieldName, processedValue);
    onInputChange(fieldName, processedValue);
  };

  // Custom styles for compact layout
  const formItemStyle = {
    marginBottom: 8,
  };

  const inputStyle = {
    width: '100%',
    minHeight: 32,
  };

  return (
    <Form
      form={form}
      layout="vertical"
    >
      {showTitle && <Title level={4}>Salary and Benefits</Title>}
      <Row gutter={[16, 8]}>
        {showBaseSalary && (
          <Col {...colProps}>
            <Form.Item 
              name="baseSalary" 
              label="Annual Base Salary" 
              rules={[{ required: true }]} 
              tooltip="Enter your annual base salary before tax and pension contributions"
              style={formItemStyle}
            >
              <InputNumber
                style={inputStyle}
                onChange={(value) => handleChange('baseSalary', value)}
                formatter={(value) => `£ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => value.replace(/£\s?|(,*)/g, '')}
                min={0}
              />
            </Form.Item>
          </Col>
        )}

        {showEmployeePensionContribution && (
          <Col {...colProps}>
            <Form.Item 
              name="employeePensionContribution"
              label="Employee Pension Contribution"
              style={formItemStyle}
            >
              <PensionContributionInput
                onChange={(value) => handleChange('employeePensionContribution', value)}
                baseSalary={form.getFieldValue('baseSalary')}
                style={inputStyle}
              />
            </Form.Item>
          </Col>
        )}

        {showEmployerMaxPensionContribution && (
          <Col {...colProps}>
            <Form.Item 
              name="employerMaxPensionContributionPct" 
              label="Employer Max Pension Contribution %" 
              tooltip="Maximum pension contribution % your employer will match"
              style={formItemStyle}
            >
              <InputNumber
                style={inputStyle}
                onChange={(value) => handleChange('employerMaxPensionContributionPct', value)}
                formatter={(value) => `${value}%`}
                parser={(value) => value.replace('%', '')}
                min={0}
                max={100}
                step={0.5}
                stringMode
              />
            </Form.Item>
          </Col>
        )}

        {showBonus && (
          <Col {...colProps}>
            <Form.Item 
              name="bonus" 
              label="Annual Bonus" 
              tooltip="Annual value of any bonus you expect for the tax year"
              style={formItemStyle}
            >
              <InputNumber
                style={inputStyle}
                onChange={(value) => handleChange('bonus', value)}
                formatter={(value) => `£ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => value.replace(/£\s?|(,*)/g, '')}
                min={0}
              />
            </Form.Item>
          </Col>
        )}

        {showMedicalBenefit && (
          <Col {...colProps}>
            <Form.Item 
              name="medicalBenefit" 
              label="Annual Medical Benefit (BIK)" 
              tooltip="From payslip: BIK medical benefit × 12"
              style={formItemStyle}
            >
              <InputNumber
                style={inputStyle}
                onChange={(value) => handleChange('medicalBenefit', value)}
                formatter={(value) => `£ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => value.replace(/£\s?|(,*)/g, '')}
                min={0}
              />
            </Form.Item>
          </Col>
        )}

        {showVoluntaryDeductions && (
          <Col {...colProps}>
            <Form.Item 
              name="voldeductions" 
              label="Annual Voluntary Deductions" 
              tooltip="Sum total of dental, life insurance and other voluntary deductions × 12"
              style={formItemStyle}
            >
              <InputNumber
                style={inputStyle}
                onChange={(value) => handleChange('voldeductions', value)}
                formatter={(value) => `£ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => value.replace(/£\s?|(,*)/g, '')}
                min={0}
              />
            </Form.Item>
          </Col>
        )}
      </Row>
    </Form>
  );
};

export default CalculatorForm;